import React from 'react';
import { allDays, blocksFromAnswers, piecesFrom, type JumpTarget, type SetupAnswers } from '../../utils/clientSetup';
import { motion } from 'framer-motion';
import { dayMonth } from '../../utils/clientDates';

/**
 * Their page, filling in as the questions are answered — and the way to
 * move around the flow.
 *
 * **Every block is a door (15 Oct 2026).** Tom: *"it's a picture. If
 * tapping a block in it jumped to the question behind it, the preview
 * becomes the navigation — and anyone who knows what they want can skip
 * straight there."* So the title goes to who it is for, the tiles to what
 * we are making, the dates and plan to the shoot screen, the approve
 * buttons to how they watch it.
 *
 * It is a simplification, not the real client page shrunk: at this width
 * the real components would be unreadable and the point is the shape.
 *
 * The small type here is `quiet`, not `mute`: this is a paper surface, and
 * mute measures 3.18:1 on paper — fine on ink, too faint here.
 *
 * **The dates come from `allDays` (16 Oct 2026)**, so a live stream's days
 * show here exactly as they will on the client's page. Reading
 * `answers.shoots` directly meant a job that was only a stream drew an
 * empty schedule in the preview and a full one on the real thing.
 */
function Block({
  to,
  onJump,
  children
}: {
  to?: JumpTarget;
  onJump?: (target: JumpTarget) => void;
  children: React.ReactNode;
}) {
  if (!to || !onJump) return <div data-id="element-1336" data-mp="3r9g51gf9enl">{children}</div>;
  return <button type="button" onClick={() => onJump(to)} className="block w-full text-left transition-opacity duration-150 ease-out hover:opacity-60" data-id="element-1337" data-mp="8rfbarlwhjaf">
      {children}
    </button>;
}
export function SetupPreview({
  answers,
  onJump
}: {
  answers: SetupAnswers;
  onJump?: (target: JumpTarget) => void;
}) {
  const blocks = answers.kinds.length > 0 ? blocksFromAnswers(answers) : [];
  const all = piecesFrom(answers);
  const tiles = all.slice(0, 12);
  const more = all.length - tiles.length;
  const gallery = blocks.includes('photographs');
  const days = allDays(answers);
  const first = days[0];
  return <div className="flex flex-col bg-paper px-5 pt-6 pb-5 text-ink" data-id="element-1338" data-mp="1nnynmos99b7f">
      <Block to="who" onJump={onJump} data-id="element-1339" data-mp="1ip586qtera4m">
        <p className="label-sm m-0 text-quiet" data-id="element-1340" data-mp="1iq6w3vvivr3r">{answers.client.trim() || 'Your client'}</p>
        <p className="mt-2 m-0 font-display text-[1.45rem] font-light leading-none tracking-tight" data-id="element-1341" data-mp="1drvlhixelckh">
          {answers.name.trim() || 'The job'}
        </p>
      </Block>

      {answers.outline.trim() && <p className="mt-3.5 m-0 text-[0.74rem] font-light leading-relaxed text-graphite" data-id="element-1342" data-mp="26fri6nmz34h9">
          {answers.outline.trim().slice(0, 110)}
          {answers.outline.trim().length > 110 ? '…' : ''}
        </p>}

      {all.length > 0 && answers.watching !== 'files' && <div className="mt-4" data-id="element-1343" data-mp="2u5viom4v6o41">
          <Block to="making" onJump={onJump} data-id="element-1344" data-mp="2u650hmfr9oyn">
            <div className="flex h-[72px] items-end bg-ink px-2.5 pb-2" data-id="element-1345" data-mp="2pdlkyjxicjr7">
              <p className="m-0 text-[0.66rem] font-light text-paper" data-id="element-1346" data-mp="2wp9qnhvyyets">{tiles[0].name}</p>
            </div>
          </Block>
          {answers.watching === 'approve' && <Block to="watching" onJump={onJump} data-id="element-1347" data-mp="31olc347a68hp">
              <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-1348" data-mp="36mwjl8a3s6wr">
                <span className="label-sm bg-ink px-2 py-1 text-paper" data-id="element-1349" data-mp="36pispccn6uh3">Approve</span>
                <span className="label-sm border border-ink px-2 py-1" data-id="element-1350" data-mp="3bnu0zinagwx7">Ask for a change</span>
              </div>
            </Block>}
        </div>}

      {tiles.length > 1 && <Block to="making" onJump={onJump} data-id="element-1351" data-mp="3klmech9g9uhy">
          <div className="mt-4 border-t border-ink pt-2" data-id="element-1352" data-mp="3pe7a3obx2ho4">
            <p className="label-sm m-0 text-quiet" data-id="element-1353" data-mp="3pepc71l9fq3y">{gallery ? 'Your work' : 'Your films'}</p>
            <div className="mt-2 grid grid-cols-3 gap-1.5" data-id="element-1354" data-mp="3uch1dnuisy4g">
              {tiles.map((piece, index) => <motion.div key={`${piece.kind}-${index}`} initial={{
            opacity: 0,
            scale: 0.88
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.22,
            ease: [0.23, 1, 0.32, 1],
            delay: index * 0.025
          }} data-id="element-1355" data-mp="38k0gez1rzrj" data-mpi={index}>
                  <div className="h-7 bg-rule" data-id="element-1356" data-mp="zw01i1uk0f96" />
                  <p className="mt-1 m-0 truncate text-[0.58rem] leading-tight" data-id="element-1357" data-mp="1dpqykar3m6o0">{piece.name}</p>
                </motion.div>)}
            </div>
            {more > 0 && <p className="mt-2 m-0 text-[0.6rem] text-quiet" data-id="element-1358" data-mp="lybfursnx432">and {more} more</p>}
          </div>
        </Block>}

      {blocks.includes('dates') && <Block to="during" onJump={onJump} data-id="element-1359" data-mp="h0t30qbiy4ps">
          <div className="mt-4 border-t border-ink pt-2" data-id="element-1360" data-mp="uyzr5eqo8qc9">
            <p className="label-sm m-0 text-quiet" data-id="element-1361" data-mp="v0km35gweban">What&rsquo;s coming up</p>
            <p className="mt-1.5 m-0 font-display text-[1.05rem] font-light leading-none" data-id="element-1362" data-mp="pyv9hle11pkg">
              {first ? dayMonth(first.date) : 'To be scheduled'}
            </p>
            {first && (first.location.trim() || days.length > 1) && <p className="mt-1.5 m-0 text-[0.62rem] text-quiet" data-id="element-1363" data-mp="21hfx48uijrde">
                {[first.location.trim(), days.length > 1 ? `and ${days.length - 1} more` : ''].filter(Boolean).join(' · ')}
              </p>}
          </div>
        </Block>}

      {blocks.includes('plan') && <Block to="during" onJump={onJump} data-id="element-1364" data-mp="24whiy88asg5t">
          <div className="mt-4 border-t border-ink pt-2" data-id="element-1365" data-mp="24x9b192orvrc">
            <p className="label-sm m-0 text-quiet" data-id="element-1366" data-mp="2smbt2i18fqmd">The plan</p>
            <div className="mt-1.5" data-id="element-1367" data-mp="2soy1ejvy6a5n">
              {['Kick-off call', 'Sign off the plan', 'First cuts for review'].map(row => <div key={row} className="flex items-baseline justify-between gap-3 border-b border-rule py-1.5" data-id="element-1368" data-mp="2ixk50kzalqgq">
                  <span className="text-[0.64rem]" data-id="element-1369" data-mp="3blfxtelqqy88">{row}</span>
                  <span className="label-sm text-quiet" data-id="element-1370" data-mp="3bjv5zwqt9te2">&mdash;</span>
                </div>)}
            </div>
          </div>
        </Block>}

      <Block to="tell" onJump={onJump} data-id="element-1371" data-mp="3q8obqn6fphnw">
        <div className="mt-5 border-t border-ink pt-3" data-id="element-1372" data-mp="3q7dc8zk0uw2l">
          <p className="label-sm m-0 text-quiet" data-id="element-1373" data-mp="3l8ju2ttsigw3">Anything at all</p>
          <p className="mt-1 m-0 text-[0.8rem] font-light" data-id="element-1374" data-mp="3l78qau0ch94z">Portal Productions</p>
          {answers.contacts.length > 0 && <p className="mt-2 m-0 text-[0.62rem] text-quiet" data-id="element-1375" data-mp="3c8wumdkmfl5o">
              {answers.contacts.length} to tell when something goes up
            </p>}
        </div>
      </Block>
    </div>;
}